import {unref} from 'vue';
import {GoingNewUtils} from 'xmgoing-utils';
import {validateAllTableRecords} from '@/main-page/gridCellEditor/validate/validateRecordCell';
import eventBus from '@/events/eventBus';
import GoingUtils from '@/utils/goingUtils';
import {useGridTableStore} from '@/store/modules/gridTableStore';
import {message} from 'ant-design-vue';
import {
  applySubTableRecordsDefaults,
  getRecordSubTableRecords,
  isNestedDisplaySubTableColumn,
} from '@/main-page/utils/xmSubTableColumnUtils';

/**
 * 优先使用表格运行时列（含用户自定义列），回退到设计器 dataConfig.columns
 */
export function getSubTableValidateColumns(subObj: any) {
  const gridKey = subObj?.props?.key;
  if (gridKey) {
    const gridTableStore: any = useGridTableStore();
    const runtimeCols = gridTableStore.getGridCols(gridKey);
    if (runtimeCols?.length) {
      return normalizeValidateColumns(
        runtimeCols.filter((col: any) => col.showFlag !== false && col.showFlag !== 'false'),
      );
    }
  }
  return normalizeValidateColumns(subObj?.dataConfig?.columns || []);
}

export type SubTableValidateResult = {
  valid: boolean;
  hasErrorFlag: boolean;
  firstErrorInfo: {
    rowIndex: number;
    fieldName: string;
    fieldLabel: string;
    nestedColLabel?: string;
    nestedRowIndex?: number;
    parentRowIndex?: number;
  } | null;
};

/**
 * 规范化子表列配置，保证校验能识别 dataIndex / required
 */
export function normalizeValidateColumns(columns: any[] = []) {
  return columns.map((col) => {
    const dataIndex = col.dataIndex || col.propId;
    const required =
      GoingNewUtils.parseBoolean(col.required) ||
      GoingNewUtils.parseBoolean(col.props?.required);
    return {
      ...col,
      dataIndex,
      propId: col.propId || dataIndex,
      propName: col.propName || col.title || dataIndex,
      required,
    };
  });
}

/**
 * 从表单 model 读取子表全量数据（含 JSON 字符串形式）
 */
export function getSubTableRecordsFromModel(modelObj: any, modelKey: string): any[] {
  if (!modelKey || modelObj == null) {
    return [];
  }
  const raw = unref(modelObj)[modelKey];
  if (raw == null || raw === '') {
    return [];
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return Array.isArray(raw) ? raw : [];
}

/**
 * 树形子表拍平为行列表（保留全局行号）
 */
export function flattenSubTableRecords(records: any[]): any[] {
  const list: any[] = [];
  const walk = (rows: any[]) => {
    if (!rows || !Array.isArray(rows)) {
      return;
    }
    rows.forEach((row) => {
      list.push(row);
      if (row.children?.length) {
        walk(row.children);
      }
    });
  };
  walk(records);
  return list;
}

/**
 * 从表格组件内存读取全量行数据
 */
function getGridTableRecords(gridKey: string): any[] {
  if (!gridKey) {
    return [];
  }
  const gridTableStore: any = useGridTableStore();
  const targetList: {records: any[] | null} = {records: null};
  eventBus.emit(GoingUtils.getEventCtlKey(gridKey) + '_getSubTableRecordsForValidate', targetList);
  if (targetList.records?.length) {
    return flattenSubTableRecords(targetList.records);
  }
  const tableRef = gridTableStore.getGridRefObjByKey(gridKey);
  const editorRecords = tableRef?.value?.getEditorRecords?.();
  if (editorRecords?.length) {
    return flattenSubTableRecords(editorRecords);
  }
  return [];
}

/**
 * 根据 gridKey 构建与表单校验一致的子表配置对象
 */
export function buildSubObjByGridKey(gridKey: string) {
  const gridTableStore: any = useGridTableStore();
  const gridObj = gridTableStore.getGridObjByKey(gridKey);
  const modelKey = gridTableStore[gridKey]?.modelKey || gridObj?.modelKey || '';
  const subObj: any = {
    props: {
      key: gridKey,
      label: gridObj?.label,
      clientPagin: gridObj?.clientPagin,
      pageSize: gridObj?.pageSize,
    },
    dataConfig: gridObj?.gridDataConfig || {},
  };
  if (modelKey) {
    subObj.model = {key: modelKey};
  }
  return subObj;
}

/**
 * 基于表单 model 全量校验动态子表（不依赖表格当前页 DOM）
 */
export function validateSubTableByModel(subObj: any, modelObj: any): SubTableValidateResult {
  const result: SubTableValidateResult = {
    valid: true,
    hasErrorFlag: false,
    firstErrorInfo: null,
  };
  const modelKey = subObj?.model?.key;
  const gridKey = subObj?.props?.key;
  const columns = getSubTableValidateColumns(subObj);
  if (!columns.length) {
    return result;
  }
  let records: any[] = [];
  if (gridKey) {
    records = getGridTableRecords(gridKey);
  }
  if (!records.length && modelKey) {
    records = flattenSubTableRecords(getSubTableRecordsFromModel(modelObj, modelKey));
  }
  if (!records.length) {
    return result;
  }
  // 校验前仅补齐快捷配置与自定义默认值（标量默认在 load/addRow 阶段已写入）
  applySubTableRecordsDefaults(records, columns, 'validate');
  const passed = validateAllTableRecords({
    tableDs: records,
    columns,
    onError: (error) => {
      result.valid = false;
      result.hasErrorFlag = true;
      if (!result.firstErrorInfo) {
        result.firstErrorInfo = {
          rowIndex: error.rowIndex,
          fieldName: error.fieldName,
          fieldLabel: error.fieldLabel,
          nestedColLabel: error.nestedColLabel,
          nestedRowIndex: error.nestedRowIndex,
          parentRowIndex: error.parentRowIndex,
        };
      }
    },
  });
  if (!passed) {
    result.valid = false;
    result.hasErrorFlag = true;
  }
  return result;
}

function subTableLabel(subObj: any, preferGridLabel = false) {
  const props = subObj?.props || {};
  if (preferGridLabel) {
    return props.aliasLabel || props.titlelabel || props.label || '子表';
  }
  return props.aliasLabel || props.titlelabel || '子表';
}

function buildMemoryRowTip(subObj: any, parentRowIndex: number) {
  const rowNo = parentRowIndex + 1;
  let rowTip = `第 ${rowNo} 行`;
  if (subObj?.props && GoingNewUtils.parseBoolean(subObj.props.clientPagin)) {
    const pageSize = Math.max(1, (
      subObj.props.pageSize
      || subObj.dataConfig?.pageSize
      || 10
    ) * 1);
    const pageNo = Math.floor(parentRowIndex / pageSize) + 1;
    const rowInPage = (parentRowIndex % pageSize) + 1;
    rowTip = `第 ${pageNo} 页第 ${rowInPage} 行（总第 ${rowNo} 行）`;
  }
  return rowTip;
}

function warnFieldError(errorPrefix: string, tableLabel: string, rowTip: string, error: any) {
  if (error.nestedColLabel != null && error.nestedRowIndex != null) {
    const nestedRowNo = error.nestedRowIndex + 1;
    message.warning(`${errorPrefix}校验失败，请确认${tableLabel}${rowTip}「${error.nestedColLabel}」第 ${nestedRowNo} 行的「${error.fieldLabel}」字段！`);
    return;
  }
  message.warning(`${errorPrefix}校验失败，请确认${tableLabel}${rowTip}的「${error.fieldLabel}」字段！`);
}

type SelectedRowRef = {
  selNo: number;
  id: string;
  record: any | null;
  inMemory: boolean;
  rowIndex: number;
};

/**
 * 按当前选中集合的顺序整理选中行。还在表格内存里的行带上整表下标。
 */
export function classifySelectedRows(gridKey: string, tableRecords: any[]): {empty: boolean; rows: SelectedRowRef[]} {
  const gridTableStore: any = useGridTableStore();
  const selectedIds = gridTableStore.getGridSels(gridKey) || [];
  const selectedRecords = gridTableStore.getRecordObjs(gridKey) || [];
  const idList = (Array.isArray(selectedIds) ? selectedIds : []).filter((id) => id != null && id !== '');
  const sourceIds = idList.length
    ? idList
    : (Array.isArray(selectedRecords) ? selectedRecords : [])
      .map((record) => record?.id)
      .filter((id) => id != null && id !== '');
  if (!sourceIds.length) {
    return {empty: true, rows: []};
  }
  const recordById = new Map<string, any>();
  (Array.isArray(selectedRecords) ? selectedRecords : []).forEach((record) => {
    if (record && record.id != null && record.id !== '') {
      recordById.set(String(record.id), record);
    }
  });
  const memoryById = new Map<string, {record: any; rowIndex: number}>();
  (tableRecords || []).forEach((record, rowIndex) => {
    if (record?.id == null || record.id === '') {
      return;
    }
    const key = String(record.id);
    if (!memoryById.has(key)) {
      memoryById.set(key, {record, rowIndex});
    }
  });
  const rows: SelectedRowRef[] = [];
  const seen = new Set<string>();
  let selNo = 0;
  sourceIds.forEach((rawId) => {
    const id = String(rawId);
    if (seen.has(id)) {
      return;
    }
    seen.add(id);
    selNo += 1;
    const memory = memoryById.get(id);
    if (memory) {
      rows.push({
        selNo,
        id,
        record: memory.record,
        inMemory: true,
        rowIndex: memory.rowIndex,
      });
      return;
    }
    const snapshot = recordById.get(id);
    rows.push({
      selNo,
      id,
      record: snapshot && typeof snapshot === 'object' ? snapshot : null,
      inMemory: false,
      rowIndex: -1,
    });
  });
  return {empty: rows.length === 0, rows};
}

function firstFieldError(record: any, columns: any[], rowIndex: number) {
  let found: any = null;
  validateAllTableRecords({
    tableDs: [record],
    columns,
    rowIndexBase: rowIndex,
    onError: (error) => {
      if (!found) {
        found = error;
      }
    },
  });
  return found;
}

function validateSelectedSubTable(subObj: any, errorPrefix: string) {
  const gridKey = subObj?.props?.key;
  const tableRecords = getGridTableRecords(gridKey);
  const classified = classifySelectedRows(gridKey, tableRecords);
  const markRecordIds = classified.rows.filter((row) => row.inMemory).map((row) => row.id);
  if (classified.empty) {
    message.warning('请先选择要校验的数据');
    return {valid: false, markRecordIds};
  }
  const columns = getSubTableValidateColumns(subObj);
  applySubTableRecordsDefaults(
    classified.rows.map((row) => row.record).filter(Boolean),
    columns,
    'validate',
  );
  const tableLabel = subTableLabel(subObj, true);
  const inMemory = classified.rows.filter((row) => row.inMemory).sort((a, b) => a.rowIndex - b.rowIndex);
  const offMemory = classified.rows.filter((row) => !row.inMemory).sort((a, b) => a.selNo - b.selNo);
  for (const row of inMemory) {
    if (!columns.length || !row.record) {
      continue;
    }
    const error = firstFieldError(row.record, columns, row.rowIndex);
    if (error) {
      const parentRowIndex = error.parentRowIndex ?? error.rowIndex;
      warnFieldError(errorPrefix, tableLabel, buildMemoryRowTip(subObj, parentRowIndex), error);
      return {valid: false, markRecordIds};
    }
  }
  for (const row of offMemory) {
    if (!row.record) {
      message.warning(`${errorPrefix}校验失败，选中的第 ${row.selNo} 条数据无法校验`);
      return {valid: false, markRecordIds};
    }
    if (!columns.length) {
      continue;
    }
    const error = firstFieldError(row.record, columns, 0);
    if (error) {
      warnFieldError(errorPrefix, tableLabel, `选中的第 ${row.selNo} 条数据`, error);
      return {valid: false, markRecordIds};
    }
  }
  return {valid: true, markRecordIds};
}

/**
 * 校验子表/数据表格列表（与 validateForm 共用）
 * @param subTableList 子表配置集合
 * @param modelObj 表单 model
 * @param options.errorPrefix 错误提示前缀，默认「表单的动态子表」
 * @param options.onlySelected 为真时只校验选中行
 */
export function getAllSubTableFlag(
  subTableList: any[],
  modelObj: any,
  options: {errorPrefix?: string; onlySelected?: boolean} = {},
) {
  const errorPrefix = options.errorPrefix || '表单的动态子表';
  if (options.onlySelected) {
    let flag = true;
    subTableList.forEach((subObj: any) => {
      if (!flag || !subObj?.props?.key) {
        return;
      }
      const selectedResult = validateSelectedSubTable(subObj, errorPrefix);
      if (!selectedResult.valid) {
        flag = false;
      }
    });
    return flag;
  }
  let flag = true;
  let firstError: any = null;
  let firstSubTableLabel = '';
  let firstErrorSubObj: any = null;
  subTableList.forEach((subObj: any) => {
    if (subObj && subObj.props && subObj.props.key) {
      const validateResult = validateSubTableByModel(subObj, modelObj);
      if (validateResult.hasErrorFlag === true) {
        flag = false;
        if (!firstError && validateResult.firstErrorInfo) {
          firstError = validateResult.firstErrorInfo;
          firstErrorSubObj = subObj;
          firstSubTableLabel = subTableLabel(subObj);
        }
      }
    }
  });
  if (!flag && firstError) {
    const parentRowIndex = firstError.parentRowIndex ?? firstError.rowIndex;
    warnFieldError(errorPrefix, firstSubTableLabel, buildMemoryRowTip(firstErrorSubObj, parentRowIndex), firstError);
  } else if (!flag) {
    const dataTip = errorPrefix === '表单的动态子表' ? '动态子表数据信息' : '表格数据信息';
    message.warning(`${errorPrefix}校验失败，请确认${dataTip}！`);
  }
  return flag;
}

/**
 * 只校验选中行时，当前画面上需要标红的行 id（还在表格内存里的选中行）。
 */
export function getSelectedMarkRecordIds(gridKey: string) {
  const classified = classifySelectedRows(gridKey, getGridTableRecords(gridKey));
  return classified.rows.filter((row) => row.inMemory).map((row) => row.id);
}

/**
 * 刷新表格单元格校验样式（标红）
 * recordIds 有值时只标这些行，其余可见行清掉这次的红。
 */
export function flushSubTableValidateStyle(
  subTableList: any[],
  options: {recordIds?: string[]} = {},
) {
  const limited = Array.isArray(options.recordIds);
  const selectedSet = limited ? new Set(options.recordIds.map((id) => String(id))) : null;
  subTableList.forEach((subObj: any) => {
    const gridKey = subObj?.props?.key;
    if (!gridKey) {
      return;
    }
    if (limited) {
      eventBus.emit(GoingUtils.getEventCtlKey(gridKey) + '_validateSubList', {recordIds: options.recordIds});
    } else {
      eventBus.emit(GoingUtils.getEventCtlKey(gridKey) + '_validateSubList');
    }
    const records = getGridTableRecords(gridKey);
    const columns = getSubTableValidateColumns(subObj);
    columns.filter((col) => isNestedDisplaySubTableColumn(col)).forEach((nestedCol) => {
      records.forEach((record, rowIndex) => {
        if (!getRecordSubTableRecords(record, nestedCol).length) {
          return;
        }
        const subColKey = nestedCol.propId || nestedCol.dataIndex || nestedCol.key;
        const nestedGridKey = `${gridKey}_sub_${record?.id || record?.key || rowIndex}_${subColKey}`;
        const eventKey = GoingUtils.getEventCtlKey(nestedGridKey) + '_validateSubList';
        if (!selectedSet) {
          eventBus.emit(eventKey);
          return;
        }
        const parentSelected = record?.id != null && selectedSet.has(String(record.id));
        eventBus.emit(eventKey, parentSelected ? undefined : {recordIds: []});
      });
    });
  });
}
